import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import utilsService from "api/utils/utils.service";
import { useTechPackSvgConversion } from "hooks/useTechPackSvgConversion";
import { DownloadResolution, DOWNLOAD_4K_TIMEOUT_MS, FileTypeOption, isRasterFormat, RasterFormat } from "../constants";
import { downloadFile, downloadImageInFormat, isIosDevice } from "../utils";
import { DownloadSubject, resolveDownloadBaseName, resolveDownloadName } from "../downloadName";
import { dispatchIos4kReady } from "../ios4kSave";
import { useSnackbar } from "hooks/useSnackbar";
import { trackEvent, trackDownload } from "utils/analytics";
import { EVENTS } from "utils/analytics.events";
import { EventUpscaleComplete } from "components/SocketListener/SocketListener";
import { requestOwned4kDownload, requestOwnedSvgConversion } from "components/SocketListener/ownedDownloadRequests";
import { reportError } from "utils/sentry";
import { isExternalImageUrl } from "utils/imageOrigin";
import { isPaywallRejection } from "utils/paywallRejection";
import { httpStatusOf } from "utils/httpStatusOf";

interface UseDownloadLogicProps {
  fileType: FileTypeOption;
  /** Design/post id of the downloaded image — sent in the download-log ping (REF-5664) so the
   *  backoffice can resolve the real posts.design_type (granular generation type). */
  designId?: string;
  /** Image id of the downloaded image — sent in the download-log ping (REF-5664). */
  imageId?: string;
  imageURL?: string;
  /** @deprecated No longer used — will be removed in a follow-up */
  showDownloadMenu?: boolean;
  isTechpackDownload?: boolean;
  /** "4k" routes non-4K raster images through the crystal upscaler before download. */
  resolution?: DownloadResolution;
  /** Identifies which menu fired the download in analytics params. */
  analyticsSurface?: string;
  /** REF-6450: WHAT the downloaded frame is — a `StudioImageType` on Studio / Edit Design surfaces —
   *  sent as `sub_type` in the download-log ping. Omit when the surface doesn't know it. */
  analyticsSubType?: string;
  /** User-chosen base file name (no extension). Sanitized here; empty → URL-derived default. */
  filename?: string;
  /** REF-5701: "Include AI Generated" toggle. When on, the download is processed server-side
   *  (burn "AI Generated" pill + embed AI-provenance metadata) instead of client-side. Raster-only. */
  aiLabel?: boolean;
  /** Which product this render belongs to, so the file can be named `<SKU>_<slot>` instead of the
   *  CDN object name. Optional: a surface with no product behind it (design canvas, tech pack)
   *  sends nothing and keeps the name it has today. See `../downloadName`. */
  subject?: DownloadSubject | null;
}

export const useDownloadLogic = ({ fileType, designId, imageId, imageURL, isTechpackDownload, resolution, analyticsSurface, analyticsSubType, filename, aiLabel, subject }: UseDownloadLogicProps) => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useSnackbar();
  const [isDownloading, setIsDownloading] = useState(false);
  const [showProgressPopup, setShowProgressPopup] = useState(false);
  // Owns the SVG queue: fires download_tech_pack, then polls svg_convert_status when the BE answers
  // with a job handle instead of a url ([LONG_JOB_SOCKET_FALLBACK]).
  const { convert: convertSvg } = useTechPackSvgConversion();
  // Watchdog timer for the upscale wait — cleared on completion, fires if the WS never arrives.
  const upscaleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearUpscaleTimeout = () => {
    if (upscaleTimeoutRef.current) {
      clearTimeout(upscaleTimeoutRef.current);
      upscaleTimeoutRef.current = null;
    }
  };

  // The upscale result is delivered by the global SocketListener (design_type:
  // "upscale_image"), which fetches + saves the 4K file then fires EventUpscaleComplete.
  // We only reset our local UI state here — the download itself already happened.
  useEffect(() => {
    const handleUpscaleComplete = () => {
      clearUpscaleTimeout();
      setIsDownloading(false);
      setShowProgressPopup(false);
    };

    window.addEventListener(EventUpscaleComplete, handleUpscaleComplete as EventListener);

    return () => {
      window.removeEventListener(EventUpscaleComplete, handleUpscaleComplete as EventListener);
      clearUpscaleTimeout();
    };
  }, []);

  const download = async (): Promise<void> => {
    if (isDownloading) return;

    setIsDownloading(true);

    // The base name every path below starts from: what the user typed, else `<SKU>_<slot>`, else
    // "" so the path falls back to the URL-derived default. ONE rule, in `../downloadName` —
    // this used to be `sanitizeFilename(filename)` plus a hand-written
    // `base || getFilenameFromUrl(...)` at each of four branches, which is why the single
    // download could never learn a naming rule the ZIP already had.
    const filenameBase = resolveDownloadBaseName(filename, subject);

    const isRaster = isRasterFormat(fileType.value);

    // REF-5701: route the download through the server-side processor when either
    //   (a) the "Include AI Generated" toggle is on → burn the "AI Generated" pill + embed
    //       machine-readable AI-provenance metadata, or
    //   (b) it's a Tech Pack WebP download → the downloadTechPack endpoint has no webp branch and
    //       would silently emit jpg, so we re-encode through process-download instead.
    // The processor also encodes to the chosen raster format. A plain label-off png/jpg download
    // stays fully client-side (below). Raster-only; SVG/video excluded. The 4K control is hidden
    // while the label is on, so this is always an Original-resolution download.
    const needsServerProcess = isRaster && imageURL && (aiLabel || (isTechpackDownload && fileType.value === "webp"));
    /**
     * Server-side download: the BE fetches the source and re-hosts it on app_cdn, then we save that.
     *
     * Used by the AI-label / techpack-webp routes below, and — REF-6494 — as the FALLBACK when a
     * client-side download fails on a third-party origin. A host that serves no
     * `Access-Control-Allow-Origin` makes `fetch` reject with a bare TypeError while `<img src>` keeps
     * rendering, which is why "the download button does nothing" on a Browzwear/Stylezone image.
     * Verified on prod: process-download 200s on such a url and hands back an app_cdn url the browser
     * reads fine (807,573 bytes).
     *
     * It is a FALLBACK and not a pre-emptive route on purpose. The processor re-encodes, so routing
     * every unknown host through it would cost byte preservation (and PNG metadata, and JPEG quality)
     * for external images whose host DOES allow CORS — e.g. the ROAR-ingested product sources on
     * image.defacto.com.tr. Trying direct first keeps those lossless; the only cost when CORS is
     * missing is one rejected pre-flight that transfers no bytes.
     */
    const downloadViaServer = async (url: string, surface: string): Promise<boolean> => {
      const res: any = await utilsService.processDownload({ image_url: url, file_type: fileType.value, ai_label: !!aiLabel });

      // Trap #1: 401/402/403/409/503 resolve with _handled — the interceptor already did the UX.
      if (res?._handled) return false;

      const processedUrl = res?.responseData?.download_url ?? res?.download_url;
      if (!processedUrl) throw new Error("process-download returned no URL");

      // Name the file from the SOURCE url, not the re-hosted one: `processedUrl` is an opaque
      // `processed_download/<uuid>` object. Same rule as every other branch — see `../downloadName`.
      await downloadFile(processedUrl, resolveDownloadName(url, fileType.value, filename, subject));
      void utilsService.logDownload({
        action: "download_image",
        file_type: fileType.value,
        resolution: "2k",
        image_url: url,
        design_id: designId || undefined,
        image_id: imageId || undefined,
        surface,
      });

      return true;
    };

    if (needsServerProcess && imageURL) {
      const surface = analyticsSurface ?? "download_menu";
      trackDownload(EVENTS.DOWNLOAD, { format: fileType.value, resolution: "2k", surface, is_zip: false });

      try {
        const res: any = await utilsService.processDownload({ image_url: imageURL, file_type: fileType.value, ai_label: !!aiLabel });

        // Trap #1: 401/402/403/409/503 resolve with _handled — the interceptor already did the UX.
        if (res?._handled) {
          setIsDownloading(false);

          return;
        }

        // BE wraps in SuccessDefault (RefabricProURL returns the raw body); tolerate an unwrapped shape too.
        const url = res?.responseData?.download_url ?? res?.download_url;
        if (!url) {
          throw new Error("process-download returned no URL");
        }

        await downloadFile(url, resolveDownloadName(imageURL, fileType.value, filename, subject));
        // Backoffice download log (REF-5664) — the process-download endpoint deliberately does NOT
        // log, so the FE owns it here with full surface/design/image attribution, matching the
        // client-side download path that shares this DownloadButton. Fire-and-forget.
        void utilsService.logDownload({
          action: "download_image",
          file_type: fileType.value,
          resolution: "2k",
          image_url: imageURL,
          design_id: designId || undefined,
          image_id: imageId || undefined,
          surface,
          sub_type: analyticsSubType || undefined,
        });
        showSuccess("Download completed successfully!");
      } catch (error) {
        // `[HTTP_HANDLED_RESPONSE]`: a rejected 402 already showed the paywall — reset loading only.
        if (!isPaywallRejection(error)) showError("Failed to download image. Please try again.");
      } finally {
        setIsDownloading(false);
      }

      return;
    }

    // "Download as 4K" — raster only, label off (the label path above is Original-only). Tech Pack
    // is excluded (its download endpoint returns a processed output; upscaling the source would
    // bypass that — pending BE contract).
    if (resolution === "4k" && !aiLabel && isRaster && imageURL && !isTechpackDownload) {
      const params = { surface: analyticsSurface ?? "download_menu", file_type: fileType.value, format: fileType.value, resolution: "4k" as const };
      trackEvent(EVENTS.DOWNLOAD_4K.START, params);

      try {
        // REF-6877: records the job as this tab's, so only this tab auto-downloads its result.
        const res: any = await requestOwned4kDownload({ image_path: imageURL, file_type: fileType.value });

        // Trap #1: 401/402/403/409/503 resolve with _handled — the interceptor already did the UX.
        if (res?._handled) {
          setIsDownloading(false);

          return;
        }

        // The BE wraps everything in SuccessDefault, so the fields live under responseData
        // (RefabricProURL returns the raw body). Reading res.already_4k directly was the bug:
        // already-4K images fell through to the wait-for-WS branch below, but no job is queued
        // for them → the popup hung forever (REF-5392). Tolerate an unwrapped shape too.
        const payload = res?.responseData ?? res ?? {};

        // BE says it's already 4K → save the direct URL now. No upscale ran, no credit charged.
        if (payload.already_4k && payload.download_url) {
          trackEvent(EVENTS.DOWNLOAD_4K.ALREADY_4K, params);
          setIsDownloading(false);

          // iOS: this resolves after the upscaleImage await → no gesture (REF-5796). Hand off to
          // the tap-to-save prompt instead of a (blocked) direct download.
          if (isIosDevice()) {
            dispatchIos4kReady({ resultUrl: payload.download_url, fileName: filenameBase ? `${filenameBase}.${fileType.value}` : undefined });

            return;
          }

          // Already-4K images skip the upscaler, but still honor the chosen raster format
          // (re-encode only on a real conversion) instead of relabeling the source (REF-5572).
          await downloadImageInFormat(payload.download_url, fileType.value as RasterFormat, filenameBase || undefined);
          showSuccess(t("snackbar:upscale.success"));

          return;
        }

        // Crystal-upscaler job started. The 4K result arrives over WS (SocketListener →
        // handleUpscaleImage) which triggers the save and fires EventUpscaleComplete; the
        // effect above resets isDownloading/showProgressPopup. Keep both set meanwhile.
        setShowProgressPopup(true);
        // Watchdog: if the WS never arrives, don't hang — dismiss + error so the button frees up.
        clearUpscaleTimeout();
        upscaleTimeoutRef.current = setTimeout(() => {
          upscaleTimeoutRef.current = null;
          setShowProgressPopup(false);
          setIsDownloading(false);
          showError(t("snackbar:upscale.error"));
          // REF-5737: the timeout path was invisible to analytics AND Sentry, so a stuck/lost
          // upscale left "no error log" (the reporter's flag). Log both now so we can tell a
          // slow-but-succeeding job from a genuinely lost WS.
          trackEvent(EVENTS.DOWNLOAD_4K.FAILED, { ...params, reason: "timeout" });
          // A client-side timeout is a non-HTTP / non-WS failure that nothing upstream captures
          // (no WS ever arrived), so manual capture is the correct owner here (error-handling.md).
          reportError(new Error("4K upscale timed out waiting for WS completion"), {
            source: "manual",
            featureArea: "design-canvas",
            level: "warning",
            requestPath: "designs/download-4k",
            fingerprint: ["download-4k", "timeout"],
            extras: { surface: params.surface, file_type: fileType.value, image_url: imageURL, timeout_ms: DOWNLOAD_4K_TIMEOUT_MS },
          });
        }, DOWNLOAD_4K_TIMEOUT_MS);

        return;
      } catch (error) {
        setIsDownloading(false);
        // 402 is interceptor-handled (Trap #1): the paywall popup already showed, so it's
        // not a 4K failure — skip the FAILED event and the redundant snackbar.
        if (isPaywallRejection(error)) return;
        // 500s aren't interceptor-handled for UX, so we still show the error + let the user
        // retry (no silent 2K downgrade). REF-5737: Sentry capture for the 5xx is owned by the
        // HttpClient interceptor (HttpClient.ts) — don't double-report here; analytics only.
        trackEvent(EVENTS.DOWNLOAD_4K.FAILED, { ...params, reason: "request_failed" });
        showError(t("snackbar:upscale.error"));

        return;
      }
    }

    if (isTechpackDownload) {
      if (fileType.value === "svg") {
        trackEvent(EVENTS.TECH_PACK.SVG_DOWNLOAD, { format: "svg", svg_kind: "techpack", surface: analyticsSurface ?? "download_menu" });
      }

      try {
        // SVG now goes through the Astra queue (202 + job_id, ~81 s median) while png/jpg still
        // answer synchronously; `convert` resolves both shapes, so this stays one await.
        const outcome = await convertSvg({
          img_url: imageURL ?? "",
          file_type: fileType.value,
        });

        // The interceptor already ran the UX for _handled / 402 — a snackbar here would duplicate it.
        if (outcome.kind === "handled") return;

        if (outcome.kind === "timeout") {
          // Monitoring expired; the job may still be running BE-side, so don't claim it failed and
          // don't imply a refund — and never auto-retry, which would reserve credits a second time.
          showError(t("snackbar:techpack_svg.still_preparing"));

          return;
        }

        if (outcome.kind === "failed") {
          showError(outcome.error || t("snackbar:techpack_svg.failed"));

          return;
        }

        const _imageURL = outcome.downloadUrl;
        // Prefer the user-chosen base name (extension from the selected format); else URL-derived.
        await downloadFile(_imageURL, resolveDownloadName(_imageURL, fileType.value, filename, subject));
        showSuccess("Download completed successfully!");
      } catch (error) {
        // 500s are NOT interceptor-handled (Trap #1 covers 401/402/403/409/503) — without this catch the rejection leaks and isDownloading sticks
        // `[HTTP_HANDLED_RESPONSE]`: a rejected 402 already showed the paywall, so reset loading only.
        if (!isPaywallRejection(error)) showError("Failed to download tech pack. Please try again.");
      } finally {
        setIsDownloading(false);
      }

      return;
    }

    // Direct download for non-SVG formats from CDN (standard / 2K raster + mp4). This is the
    // shared DownloadButton path used across ~12 surfaces (photoshoot, studio detail, image
    // popups, edit design, history, ghost/tryon/mannequin). Emit the canonical download event
    // here so these stop being invisible to analytics (B1 — see ga4-download-attributes.md).
    if (fileType.value !== "svg" && imageURL) {
      trackDownload(EVENTS.DOWNLOAD, { format: fileType.value, resolution: isRaster ? "2k" : undefined, surface: analyticsSurface ?? "download_menu", is_zip: false });
      // REF-5664 (Option C): this client-side CDN download hits no BE endpoint, so ping the
      // download log so it shows in the backoffice Download Logs (previously only GA4 saw it).
      // Fire-and-forget — logDownload swallows its own errors and never blocks the download.
      void utilsService.logDownload({
        action: "download_image",
        file_type: fileType.value,
        resolution: isRaster ? "2k" : undefined,
        image_url: imageURL,
        // design_id/image_id let the backoffice resolve the real posts.design_type (e.g. tryon,
        // ghost_shooting, artistic_sketch) — a granular generation type, not just the coarse surface.
        design_id: designId || undefined,
        image_id: imageId || undefined,
        surface: analyticsSurface ?? "download_menu",
        // REF-6450: the frame's own type (Studio / Edit Design pass their StudioImageType) — the
        // backoffice reads this instead of reconstructing it from result_data.
        sub_type: analyticsSubType || undefined,
      });
      try {
        if (isRaster) {
          // Save in the chosen raster format. downloadImageInFormat re-encodes only when the
          // source isn't already that format, so "Download as JPG" on a PNG converts (REF-5572)
          // while a same-format download keeps the original bytes (no lossy recompression).
          await downloadImageInFormat(imageURL, fileType.value as RasterFormat, filenameBase || undefined);
        } else {
          // Video (mp4) and any other non-raster format download as-is — no re-encode.
          // Prefer the user-chosen base name (extension from the selected format); else URL-derived.
          await downloadFile(imageURL, resolveDownloadName(imageURL, fileType.value, filename, subject));
        }
        showSuccess("Download completed successfully!");
      } catch (error) {
        // REF-6494: a third-party origin that sends no `Access-Control-Allow-Origin` makes the
        // `fetch` above reject with a bare TypeError — the image is on screen the whole time, so
        // this read as "the download button does nothing". Retry through the BE, which has no
        // same-origin restriction. Same-origin/app_cdn failures are real failures and are NOT
        // retried; nor is iOS, whose client path returns early inside its user-activation window
        // (openInNewTab) and must not be pushed behind another await (REF-5796).
        const canRetryViaServer = isRaster && !isIosDevice() && isExternalImageUrl(imageURL);

        if (canRetryViaServer) {
          try {
            const saved = await downloadViaServer(imageURL, analyticsSurface ?? "download_menu");
            if (saved) {
              showSuccess("Download completed successfully!");
              setIsDownloading(false);

              return;
            }
            // `saved === false` means the response was interceptor-handled — its UX already ran.
            setIsDownloading(false);

            return;
          } catch (fallbackError) {
            // 402 REJECTS (Trap #1 covers 401/402/403/409/503 as *resolved*, but 402 rejects) and the
            // paywall snackbar was already dispatched by the interceptor — a second one here is a
            // duplicate. Reset loading only, same rule the 4K branch above follows.
            if (!isPaywallRejection(fallbackError)) {
              showError("Failed to download image. Please try again.");
            }
            setIsDownloading(false);

            return;
          }
        }

        showError("Failed to download image. Please try again.");
      } finally {
        setIsDownloading(false);
      }

      return;
    }

    // Show progress popup for SVG conversion
    setShowProgressPopup(true);

    try {
      // REF-6877: records the request as this tab's, so only this tab auto-downloads its result.
      await requestOwnedSvgConversion(imageURL ?? "");

      return;
    } catch (error) {
      setIsDownloading(false);
      setShowProgressPopup(false);
      console.error("Error converting to SVG", { status: httpStatusOf(error) });

      let errorMessage = "Download failed. Please try again.";
      if (error instanceof Error) {
        if (error.message.includes("network") || error.message.includes("fetch")) {
          errorMessage = "Network error. Please check your connection and try again.";
        }
      }

      // `[HTTP_HANDLED_RESPONSE]`: a rejected 402 already showed the paywall — reset the popup and
      // loading state above, but stay silent rather than stacking a generic download error on it.
      if (!isPaywallRejection(error)) showError(errorMessage);
    }
  };

  return {
    isDownloading,
    showProgressPopup,
    download,
  };
};
